import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import {
  InputText,
  InputType,
  SectionTitle,
} from '@insights-ltd/design-library/components';
import CheckboxGroup from 'components/CheckboxGroup/CheckboxGroup';
import useGetProducts from 'components/hooks/useGetProducts';
import { Control, FieldValues, Path } from 'react-hook-form';
import { Box, Button, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import UnescapedTrans from 'components/i18n/UnescapedTrans';
import { PermissionRole } from 'pages/AddUser/AddUserForm/types';
import { useAuth } from 'contexts/AuthContext';
import { LicensedProduct, Option } from 'types/types';
import { useBetaEnabled } from 'features';
import { useEffect } from 'react';
import RolePermissionV2 from 'pages/EditAccount/EditAccountForm/RolePermissionV2';
import { OrganisationResponse } from 'api/httpEntities';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type UserDetailsSectionProps<T extends FieldValues> = {
  email: string;
  control: Control<T>;
  permissionRole: PermissionRole;
  setPermissionRole: any;
  setValue: any;
  existingPractitioner: any | undefined;
  isInsightsOrgSelected?: boolean;
  selectedOrgs?: OrganisationResponse[];
};

const StyledPersonIcon = styled(PersonIcon)(({ theme }) => ({
  fill: theme.palette.pink.main,
  height: '20px',
}));

const StyledP = styled('p')({
  lineHeight: '28px',
  color: '#6D6D6D',
  marginTop: '-1.5rem',
});

const StyledInputText = styled(InputText)(({ theme }) => ({
  '& > label': {
    color: theme.palette.text.primary,
  },
  marginBottom: '1rem',
}));

const StyledDiv = styled('div')({
  marginBottom: '0.5rem',
});

const UserDetailsSection = <T extends FieldValues>({
  email,
  permissionRole,
  setPermissionRole,
  control,
  setValue,
  existingPractitioner,
  isInsightsOrgSelected = false,
  selectedOrgs,
}: UserDetailsSectionProps<T>) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasUpdateRolesPermission = user?.permissions.Practitioner_UpdateRoles;

  const isPersonalImpactProfileBetaEnabled = useBetaEnabled(
    'personal-impact-profile',
  );
  const products = useGetProducts();

  const displayedProducts = isPersonalImpactProfileBetaEnabled
    ? Object.entries(products)
    : Object.entries(products).filter(
        ([key]) => key !== 'DISCOVERY_IMPACT_PROFILE',
      );

  useEffect(() => {
    const currentValues = control._formValues.licensedProducts || [];
    const hasPersonalImpact = currentValues.includes(
      'DISCOVERY_IMPACT_PROFILE',
    );
    // Add product is beta true and there is no product
    if (isPersonalImpactProfileBetaEnabled && !hasPersonalImpact) {
      setValue(
        'licensedProducts' as Path<T>,
        [...currentValues, 'DISCOVERY_IMPACT_PROFILE'] as any,
      );
    }

    if (!isPersonalImpactProfileBetaEnabled && hasPersonalImpact) {
      setValue(
        'licensedProducts' as Path<T>,
        currentValues.filter(
          (p: string) => p !== 'DISCOVERY_IMPACT_PROFILE',
        ) as any,
      );
    }
  }, [isPersonalImpactProfileBetaEnabled, control, setValue]);

  return (
    <div data-testid="create-user-details">
      <Box
        sx={{
          mt: spacingSizeMap.L,
          mb: spacingSizeMap.XL,
        }}
      >
        <SectionTitle
          icon={{ icon: <StyledPersonIcon />, iconColour: 'pink' }}
          text={t('ui.event-management.users.create.details')}
        />
      </Box>
      <StyledP>
        <UnescapedTrans i18nKey="ui.event-management.users.invite.header.description" />
      </StyledP>
      <StyledInputText
        id="emailAddress"
        label={t('ui.event-management.users.create.email')}
        placeholder={email}
        type={InputType.EMAIL}
        required
        disabled
        fullWidth
      />
      <CheckboxGroup
        control={control}
        rules={{
          required: true,
          validate: { CustomMinLength: (v) => v.length > 0 },
        }}
        name={'licensedProducts' as Path<T>}
        title={t('ui.event-management.products.title')}
        errorMessage={t('ui.event-management.products.error-required')}
        error={false}
        options={displayedProducts.map(
          ([value, option]: [string, Option<LicensedProduct>]) => ({
            value: value as LicensedProduct,
            label: t(option.label),
            disabled: option.disabled,
          }),
        )}
        setValue={setValue}
      />
      <StyledDiv />
      {(!existingPractitioner || hasUpdateRolesPermission) && (
        <RolePermissionV2
          isSuperAdmin={isInsightsOrgSelected}
          roleChangeIsPermitted={!!hasUpdateRolesPermission}
          permissionRole={permissionRole}
          changeRoleType={setPermissionRole}
          organisationId={selectedOrgs?.[0]?.id || ''}
        />
      )}
      <Box
        sx={{
          mt: '40px',
        }}
      >
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <div />
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
          >
            {t('ui.event-management.users.create.crumb')}
          </Button>
        </Grid>
      </Box>
    </div>
  );
};

export default UserDetailsSection;
